import { Box, Button } from "@material-ui/core";
import { ContainerPage, OptionsMenu, TitlePage } from "components";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { usePopup } from "hooks/usePopup";
import { useTableParams } from "hooks/useTableParams";
import { useCallback, useEffect, useState } from "react";
import { FiEdit2, FiSlash } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import productBaseService from "services/productBaseService";
import stockRuleService from "services/stockRuleService";
import { ConfirmationPopup, Table } from "shared";

import { TabReceipt } from "../Receivement/components/TabReceipt";
import { RuleDrawer } from "./components/RuleDrawer";
import { StatusChip, useStyles } from "./styles";

const STATUS_LABELS = {
  scheduled: "Agendada",
  active: "Ativa",
  finished: "Encerrada",
  cancelled: "Cancelada"
};

const formatDate = value =>
  value
    ? format(new Date(value), "dd/MM/yyyy HH:mm", { locale: ptBR })
    : "Sem término";

const RulesTable = ({ scope, refreshCounter, onEdit }) => {
  const { addPopup } = usePopup();
  const [ruleToCancel, setRuleToCancel] = useState(null);
  const { data, onChange, params, refresh, status, handleSearch, count } =
    useTableParams({
      paramsDefault: { scope },
      service: requestParams => stockRuleService.list(requestParams)
    });

  useEffect(() => {
    if (refreshCounter) refresh();
  }, [refreshCounter]);

  const closeCancelPopup = useCallback(() => {
    setRuleToCancel(null);
  }, []);

  const cancelRule = useCallback(async () => {
    if (!ruleToCancel) return;
    try {
      await stockRuleService.cancel(ruleToCancel.id);
      addPopup({ type: "success", title: "Regra cancelada com sucesso." });
      refresh();
    } catch {
      addPopup({ type: "error", title: "Erro ao cancelar regra." });
    }
  }, [addPopup, refresh, ruleToCancel]);

  const columns = [
    {
      field: "product_base_name",
      label: "Produto",
      align: "left"
    },
    {
      field: "strategy_name",
      label: "Regra",
      align: "left"
    },
    {
      field: "starts_at",
      label: "Início",
      formatter: formatDate
    },
    {
      field: "ends_at",
      label: "Fim",
      formatter: formatDate
    },
    {
      field: "status",
      label: "Status",
      formatter: value => (
        <StatusChip
          size="small"
          label={STATUS_LABELS[value] || value}
          status={value}
        />
      )
    },
    {
      field: "created_by_name",
      label: "Criado por"
    },
    {
      field: "updated_by_name",
      label: "Última alteração"
    },
    {
      field: "",
      label: "",
      renderCell: ({ data: rule }) => {
        const options = [];
        if (rule.status === "scheduled") {
          options.push({
            title: "Editar",
            icon: FiEdit2,
            permissions: ["Estoque", "Tech", "Admin", "Stock View"],
            action: () => onEdit(rule)
          });
        }
        if (rule.status === "scheduled" || rule.status === "active") {
          options.push({
            title: "Cancelar",
            icon: FiSlash,
            permissions: ["Estoque", "Tech", "Admin", "Stock View"],
            action: () => setRuleToCancel(rule)
          });
        }
        return options.length ? (
          <OptionsMenu data={rule} options={options} />
        ) : null;
      }
    }
  ];

  return (
    <>
      <Table
        handleSearch={handleSearch}
        columns={columns}
        data={data}
        onChange={onChange}
        params={params}
        status={status}
        refresh={refresh}
        count={count}
        placeholder="Pesquisar produto"
        filterProps={{
          label: "Regra",
          parameter: "strategy",
          values: [
            { id: "infinite_reopening", label: "Reabertura infinita" },
            {
              id: "anticipate_scheduled",
              label: "Antecipar débitos agendados"
            }
          ]
        }}
      />

      <ConfirmationPopup
        isOpen={!!ruleToCancel}
        onClose={closeCancelPopup}
        onConfirm={cancelRule}
        title="Cancelar regra de estoque"
        message={`Deseja cancelar a regra de ${
          ruleToCancel?.product_base_name || ""
        }?`}
        description="A regra será movida para o histórico e deixará de ser aplicada."
        confirmText="Cancelar regra"
        cancelText="Voltar"
        localStorageKey="hideStockRuleCancellationConfirmation"
        confirmButtonProps={{ color: "secondary" }}
      />
    </>
  );
};

export const StockRules = () => {
  const classes = useStyles();
  const { addPopup } = usePopup();
  const [scope, setScope] = useState("current");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [productList, setProductList] = useState([]);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const { data } = await productBaseService.getProductBase({
          status: "active",
          pageSize: 4000
        });
        setProductList(data.results);
      } catch {
        addPopup({
          type: "error",
          title: "Erro ao carregar produtos base."
        });
      }
    };
    loadProducts();
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRule(null);
  };

  return (
    <ContainerPage>
      <Box className={classes.header}>
        <TitlePage>Regras de estoque</TitlePage>
        <Button
          variant="contained"
          startIcon={<IoMdAdd />}
          className={classes.addButton}
          onClick={() => setDrawerOpen(true)}
        >
          Adicionar
        </Button>
      </Box>

      <TabReceipt
        activeTab={scope}
        handleChangeTable={(_, value) => setScope(value)}
        tabs={[
          { label: "Regras atuais", value: "current" },
          { label: "Histórico", value: "history" }
        ]}
      />

      <RulesTable
        key={scope}
        scope={scope}
        refreshCounter={refreshCounter}
        onEdit={rule => {
          setEditingRule(rule);
          setDrawerOpen(true);
        }}
      />

      <RuleDrawer
        open={drawerOpen}
        close={closeDrawer}
        initialValues={editingRule}
        productList={productList}
        onSaved={() => setRefreshCounter(value => value + 1)}
      />
    </ContainerPage>
  );
};
